import React from 'react';
import BalanceIcon from '@mui/icons-material/Balance';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { useSupplierPrepaymentReconciliationPage } from '../Hooks/useSupplierPrepaymentReconciliationPage';
import { LedgerReconciliationRowDto, InvoiceSettlementReconciliationRowDto } from '../Types/bankDocument.types';

const fmt = (amount: number, currency: string): string => `${amount.toFixed(2)} ${currency}`;

const fmtDate = (value: string): string => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('pl-PL');
};

const BalanceChip: React.FC<{ isBalanced: boolean }> = ({ isBalanced }) =>
  isBalanced ? (
    <Chip
      icon={<CheckCircleOutlineIcon />}
      label="OK"
      color="success"
      size="small"
      variant="outlined"
    />
  ) : (
    <Chip
      icon={<ErrorOutlineIcon />}
      label="ROZBIEŻNOŚĆ"
      color="error"
      size="small"
    />
  );

const LedgerTable: React.FC<{ rows: LedgerReconciliationRowDto[] }> = ({ rows }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Ledger ID</TableCell>
        <TableCell>Waluta</TableCell>
        <TableCell align="right">Saldo faktyczne</TableCell>
        <TableCell align="right">Depozyty</TableCell>
        <TableCell align="right">Alokacje</TableCell>
        <TableCell align="right">Zwolnienia</TableCell>
        <TableCell align="right">Refundy</TableCell>
        <TableCell align="right">Korekty ręczne</TableCell>
        <TableCell align="right">Saldo oczekiwane</TableCell>
        <TableCell align="right">Rozbieżność</TableCell>
        <TableCell>Status</TableCell>
        <TableCell align="right">Ruchów</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow
          key={row.ledgerId}
          sx={{ bgcolor: row.isBalanced ? undefined : 'error.lighter' }}
        >
          <TableCell>{row.ledgerId}</TableCell>
          <TableCell>{row.currency}</TableCell>
          <TableCell align="right">{fmt(row.actualOpenBalance, row.currency)}</TableCell>
          <TableCell align="right" sx={{ color: 'success.main' }}>
            {fmt(row.sumDeposits, row.currency)}
          </TableCell>
          <TableCell align="right" sx={{ color: 'error.main' }}>
            {fmt(row.sumAllocations, row.currency)}
          </TableCell>
          <TableCell align="right" sx={{ color: 'success.main' }}>
            {fmt(row.sumReleases, row.currency)}
          </TableCell>
          <TableCell align="right" sx={{ color: row.sumRefunds !== 0 ? 'error.main' : undefined }}>
            {fmt(row.sumRefunds, row.currency)}
          </TableCell>
          <TableCell align="right">{fmt(row.sumManualAdjustments, row.currency)}</TableCell>
          <TableCell align="right">{fmt(row.expectedBalance, row.currency)}</TableCell>
          <TableCell align="right">
            <Tooltip title={row.isBalanced ? 'Brak rozbieżności' : 'Saldo ledgera ≠ suma ruchów'}>
              <Typography
                variant="body2"
                color={row.isBalanced ? 'text.primary' : 'error.main'}
                fontWeight={row.isBalanced ? undefined : 'bold'}
              >
                {fmt(row.discrepancy, row.currency)}
              </Typography>
            </Tooltip>
          </TableCell>
          <TableCell>
            <BalanceChip isBalanced={row.isBalanced} />
          </TableCell>
          <TableCell align="right">{row.totalMovements}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const InvoiceTable: React.FC<{ rows: InvoiceSettlementReconciliationRowDto[] }> = ({ rows }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>FV ID</TableCell>
        <TableCell>Waluta</TableCell>
        <TableCell align="right">TotalGross</TableCell>
        <TableCell align="right">Zapłacono</TableCell>
        <TableCell align="right">Z zaliczki</TableCell>
        <TableCell align="right">Bezpośrednio</TableCell>
        <TableCell align="right">Pozostało</TableCell>
        <TableCell align="right">Rozliczeń</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.expenseInvoiceId}>
          <TableCell>{row.expenseInvoiceId}</TableCell>
          <TableCell>{row.currency}</TableCell>
          <TableCell align="right">{fmt(row.totalGross, row.currency)}</TableCell>
          <TableCell align="right">{fmt(row.actualPaidAmount, row.currency)}</TableCell>
          <TableCell align="right" sx={{ color: 'primary.main' }}>
            {fmt(row.sumSettledByPrepayment, row.currency)}
          </TableCell>
          <TableCell align="right">
            {row.directPaymentAmount > 0 ? (
              <Tooltip title="Kwota zapłacona bezpośrednio z banku (poza mechanizmem zaliczki)">
                <Typography variant="body2" color="text.secondary">
                  {fmt(row.directPaymentAmount, row.currency)}
                </Typography>
              </Tooltip>
            ) : (
              '—'
            )}
          </TableCell>
          <TableCell align="right">
            <Typography
              variant="body2"
              color={row.remainingDue > 0 ? 'warning.main' : 'success.main'}
            >
              {fmt(row.remainingDue, row.currency)}
            </Typography>
          </TableCell>
          <TableCell align="right">{row.settlementCount}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const SupplierPrepaymentReconciliationPage: React.FC = () => {
  const {
    apContractors,
    selectedApContractorId,
    setSelectedApContractorId,
    reconciliation,
    isLoading,
    isFetching,
    errorMessage,
    refetch,
  } = useSupplierPrepaymentReconciliationPage();

  return (
    <Stack spacing={3}>
      <CustomCard
        icon={<BalanceIcon color="primary" fontSize="large" />}
        title="Rekoncyliacja prepayment"
        subheader="Weryfikacja integralności sald i rozliczeń dostawców"
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="reconciliation-ap-contractor">AP kontrahent</InputLabel>
            <Select
              labelId="reconciliation-ap-contractor"
              label="AP kontrahent"
              value={selectedApContractorId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedApContractorId(v ? Number(v) : undefined);
              }}
            >
              <MenuItem value="">Wybierz AP kontrahenta</MenuItem>
              {apContractors.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} ({c.nip})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching || !selectedApContractorId}
          >
            Odśwież
          </Button>

          {reconciliation && (
            <Box>
              {reconciliation.isFullyBalanced ? (
                <Chip
                  icon={<CheckCircleOutlineIcon />}
                  label="Wszystkie ledgery zbilansowane"
                  color="success"
                />
              ) : (
                <Chip
                  icon={<ErrorOutlineIcon />}
                  label="Wykryto rozbieżności"
                  color="error"
                />
              )}
            </Box>
          )}

          {reconciliation && (
            <Typography variant="caption" color="text.secondary">
              Wygenerowano: {fmtDate(reconciliation.generatedAtUtc)}
            </Typography>
          )}
        </Stack>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </CustomCard>

      {!isLoading && reconciliation && (
        <>
          <CustomCard
            icon={<BalanceIcon color="primary" />}
            title="Poziom 1 — Integralność ledgerów"
            subheader="Saldo faktyczne vs suma ruchów w timeline"
          >
            {reconciliation.ledgerRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Brak ledgerów dla wybranego kontrahenta.
              </Typography>
            ) : (
              <LedgerTable rows={reconciliation.ledgerRows} />
            )}
          </CustomCard>

          <CustomCard
            icon={<BalanceIcon color="primary" />}
            title="Poziom 2 — Rozliczenia faktur kosztowych"
            subheader="Faktury rozliczone z zaliczki — PaidAmount vs suma rekordów rozliczeń"
          >
            {reconciliation.invoiceRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Brak faktur rozliczonych z zaliczki dla wybranego kontrahenta.
              </Typography>
            ) : (
              <InvoiceTable rows={reconciliation.invoiceRows} />
            )}
          </CustomCard>
        </>
      )}
    </Stack>
  );
};

export default SupplierPrepaymentReconciliationPage;
